import React from 'react';
import {
  TextField,
  Autocomplete,
  InputAdornment,
  Chip,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  createFilterOptions
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  FaFilter, FaRedo, FaSearch,
  FaBuilding, FaProjectDiagram, FaUser, FaClock, FaFlag, FaUsers
} from "react-icons/fa";
import CustomButton from "../Custom/CustomButton";
import DateRangeFilter from "./DateRangeFilter";
import ActiveFilterPill from './ActiveFilterPill';
import TaskSummaryCards from '../dashboard/TaskSummaryCards';
import TaskStatusFilterRow from './TaskStatusFilterRow';

const employeeFilterOptions = createFilterOptions({
  limit: 100,
  stringify: (o) =>
    `${o.first_name || o.FirstName || o.firstName || ""} ${o.last_name || o.LastName || o.lastName || ""} ${o.email || o.Email || ""}`.trim(),
});

const TaskPageHeader = ({
  title,
  tabs = [],
  activeTab,
  onTabChange,
  buttons = [],
  summaryCards = [],
  onOpenFilters,
  activeFilterCount = 0,
  statusSummary = [],
  currentStatus = "All",
  onStatusFilter,
  tasks = [],
  filters = {},
  onFilterChange,
  globalFilter = "",
  onGlobalFilterChange,
  onApply,
  onReset,
  activePills = [],
  statusCounts = null,
  isApplying = false,
  createdByCreators = [],
  selectedCreatedByUserId = null,
  currentUserId = null,
  onCreatedBySelect,
  hideStatusRow = false,
}) => {
  return (
    <div className="task-page-header-container">
      {/* Row 1 (Title + Buttons) */}
      <div className="task-page-header-top">
        {tabs.length > 0 ? (
          <div className="task-page-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                role="tab"
                type="button"
                aria-selected={activeTab === tab.value}
                className={`task-page-tab${activeTab === tab.value ? " active" : ""}`}
                onClick={() => onTabChange && onTabChange(tab.value)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : (
          <h2 className="task-page-title">{title}</h2>
        )}
        <div className="task-header-btns">
          {onGlobalFilterChange && (
            <TextField
              className="compact-header-input header-top-search"
              placeholder="Search tasks by name..."
              size="small"
              value={globalFilter}
              onChange={(e) => onGlobalFilterChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (onApply) onApply();
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ pl: 0.5 }}>
                    <FaSearch color="#aaa" size={14} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          {buttons.map((btn, idx) => {
            if (btn.iconOnly) {
              return (
                <Tooltip key={idx} title={btn.label} arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={btn.onClick}
                      disabled={btn.disabled || isApplying || btn.loading}
                      sx={{
                        width: 28,
                        height: 28,
                        border: "1px solid #e0e4e8",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        color: "#1e293b",
                        "&:hover": { backgroundColor: "#f9fafb", borderColor: "#d1d5db" },
                        "&.Mui-disabled": { backgroundColor: "#f9fafb", borderColor: "#e8eaed" },
                        ...btn.sx,
                      }}
                    >
                      {btn.loading ? <CircularProgress size={16} color="inherit" /> : btn.icon}
                    </IconButton>
                  </span>
                </Tooltip>
              );
            }

            return (
              <CustomButton
                key={idx}
                variant={btn.variant || "contained"}
                color={btn.color || "error"}
                startIcon={btn.icon}
                size="small"
                className="compact-header-btn"
                onClick={btn.onClick}
                loading={btn.loading}
                disabled={btn.disabled || isApplying}
                fullWidth={false}
                sx={{ textTransform: 'none', borderRadius: '6px', whiteSpace: 'nowrap', minWidth: 'max-content', ...btn.sx }}
              >
                {btn.label}
              </CustomButton>
            );
          })}
        </div>
      </div>

      <div className="task-page-filters-row">
        {/* Filters Row */}
        {onOpenFilters ? (
          <div className="filters-button-row">
            <div className="filters-button-row-left">
              <CustomButton
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
                size="small"
                className="compact-header-btn"
                onClick={(e) => onOpenFilters(e)}
                fullWidth={false}
                sx={{
                  textTransform: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#fff',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  flexShrink: 0,
                  '&:hover': { backgroundColor: '#f9fafb', borderColor: '#9ca3af' },
                }}
              >
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
              </CustomButton>
              {activePills.length > 0 && (
                <Box className="filters-inline-chips" sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                  {activePills.map((pill, idx) => (
                      <ActiveFilterPill
                        key={pill.id || idx}
                        label={pill.label}
                        onDelete={pill.onDelete}
                      />
                    ))}
                </Box>
              )}
            </div>
            {filters.targetEndDate !== undefined && (
              <div className="filters-date-range">
                <DateRangeFilter
                  value={filters.targetEndDate.value || {}}
                  onChange={(rangeVal) => onFilterChange("targetEndDate", rangeVal)}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="responsive-filters-grid">
            {filters.departments && (
              <Autocomplete
                className="filter-autocomplete-input"
                size="small"
                options={filters.departments.options}
                getOptionLabel={(o) => o.name || ""}
                isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
                value={filters.departments.value}
                onChange={(_, v) => onFilterChange("department", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Departments"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start" sx={{ pl: 0.5 }}>
                            <FaBuilding color="#888" size={14} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {filters.projects && (
              <Autocomplete
                className="filter-autocomplete-input"
                size="small"
                options={filters.projects.options}
                getOptionLabel={(o) => o.name || ""}
                isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
                value={filters.projects.value}
                onChange={(_, v) => onFilterChange("project", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Projects"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start" sx={{ pl: 0.5 }}>
                            <FaProjectDiagram color="#888" size={14} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {filters.groups && (
              <Autocomplete
                className="filter-autocomplete-input"
                size="small"
                options={filters.groups.options}
                getOptionLabel={(o) => o?.groupName || o?.name || ""}
                isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
                value={filters.groups.value}
                onChange={(_, v) => onFilterChange("group", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Groups"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start" sx={{ pl: 0.5 }}>
                            <FaUsers color="#888" size={14} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {filters.employees && (
              <Autocomplete
                className="filter-autocomplete-input"
                size="small"
                options={filters.employees.options}
                filterOptions={employeeFilterOptions}
                getOptionLabel={(o) => {
                  const fn = o.first_name || o.FirstName || o.firstName || "";
                  const ln = o.last_name || o.LastName || o.lastName || "";
                  const name = `${fn} ${ln}`.trim();
                  return name || o.email || o.Email || "";
                }}
                isOptionEqualToValue={(opt, val) => (opt.id || opt.userId || opt.value) === (val.id || val.userId || val.value)}
                value={filters.employees.value}
                onChange={(_, v) => onFilterChange("employee", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All Employees"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start" sx={{ pl: 0.5 }}>
                            <FaUser color="#888" size={14} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {filters.ageing && (
              <Autocomplete
                className="filter-autocomplete-input"
                size="small"
                options={filters.ageing.options}
                getOptionLabel={(o) => o.label || ""}
                isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
                value={filters.ageing.value}
                onChange={(_, v) => onFilterChange("ageing", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Over Due (All)"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start" sx={{ pl: 0.5 }}>
                            <FaClock color="#888" size={14} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
            {filters.priority && (
              <Autocomplete
                className="filter-autocomplete-input"
                size="small"
                options={filters.priority.options}
                getOptionLabel={(o) => o.label || ""}
                isOptionEqualToValue={(opt, val) => (opt.id || opt.value) === (val.id || val.value)}
                value={filters.priority.value}
                onChange={(_, v) => onFilterChange("priority", v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Priority (All)"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start" sx={{ pl: 0.5 }}>
                            <FaFlag color="#888" size={14} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </div>
        )}
        {!onOpenFilters && (
          <div className="task-filters-search-row">
            {filters.targetEndDate !== undefined && (
              <DateRangeFilter
                value={filters.targetEndDate.value || {}}
                onChange={(rangeVal) => onFilterChange('targetEndDate', rangeVal)}
              />
            )}
            <div className="task-filter-btns">
              <CustomButton
                variant="outlined"
                color="error"
                startIcon={<FaFilter size={14} />}
                size="small"
                className="compact-header-btn"
                onClick={onApply}
                loading={isApplying}
                loadingText="Applying..."
                fullWidth={false}
                sx={{
                  textTransform: 'none',
                  borderRadius: '6px',
                  border: '1px solid #d32f2f',
                  color: '#d32f2f',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    borderColor: '#d32f2f'
                  }
                }}
              >
                Apply
              </CustomButton>
              <CustomButton
                variant="contained"
                color="inherit"
                startIcon={<FaRedo size={14} />}
                size="small"
                className="compact-header-btn"
                onClick={onReset}
                disabled={isApplying}
                fullWidth={false}
                sx={{
                  textTransform: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#f1f3f5',
                  color: '#111827',
                  border: '1px solid #e0e4e8',
                  '&:hover': { backgroundColor: '#e5e7eb', borderColor: '#d1d5db' }
                }}
              >
                Reset
              </CustomButton>
            </div>
          </div>
        )}
      </div>
      {/* Active filter chips (legacy filter row only) */}
      {!onOpenFilters && activePills.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, pb: 1, flexWrap: "wrap" }}>
          {activePills.map((pill, idx) => (
              <ActiveFilterPill
                key={pill.id || idx}
                label={pill.label}
                onDelete={pill.onDelete}
              />
            ))}
        </Box>
      )}

      <TaskSummaryCards cards={summaryCards} />

      {!hideStatusRow && (
        <TaskStatusFilterRow
          statusSummary={statusSummary}
          currentStatus={currentStatus}
          onStatusFilter={onStatusFilter}
          tasks={tasks}
          statusCounts={statusCounts}
          isApplying={isApplying}
          createdByCreators={createdByCreators}
          selectedCreatedByUserId={selectedCreatedByUserId}
          currentUserId={currentUserId}
          onCreatedBySelect={onCreatedBySelect}
        />
      )}
    </div>
  );
};

export default TaskPageHeader;

